import { useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { WorkspaceBadge } from "@/components/ui/WorkspaceBadge";
import { SubmitTicketDialog } from "@/components/submit/SubmitTicketDialog";
import { useIdeaTargets } from "@/hooks/useIdeaTargets";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { resolveWorkspaceColor } from "@/lib/colors";
import { t } from "@/lib/strings";
import type { RequestForm } from "@/lib/types";

/**
 * Home section that surfaces the company Ideenbox(en) published to Home
 * (home_listed). Hidden when none are published — no dead empty state.
 */
export function IdeaPickerSection() {
  const isDesktop = useBreakpoint("md");
  const { data: targets = [], isLoading } = useIdeaTargets();
  const [active, setActive] = useState<RequestForm | null>(null);

  if (isLoading || targets.length === 0) return null;

  return (
    <div style={{ marginBottom: 36 }}>
      <div style={{ margin: "0 0 12px" }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "var(--ink-1)",
            letterSpacing: "-0.01em",
          }}
        >
          {t.homeIdea}
        </div>
        <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
          {t.ideaPickerSubtitle}
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isDesktop ? "repeat(3, 1fr)" : "1fr",
          gap: 12,
        }}
      >
        {targets.map((b) => {
          const color = resolveWorkspaceColor({ id: b.workspace_id, color: b.workspace_color });
          return (
            <button
              key={b.id}
              type="button"
              onClick={() => setActive(b)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "14px 16px",
                borderRadius: 10,
                border: "1px solid var(--hairline)",
                background: "var(--surface)",
                cursor: "pointer",
                textAlign: "left",
                fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
                color: "var(--ink-1)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  background: color,
                }}
              />
              <WorkspaceBadge
                name={b.workspace_name}
                icon={b.workspace_icon}
                color={color}
                size={36}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {b.name}
                </div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: "var(--ink-3)",
                    marginTop: 2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {b.workspace_name}
                </div>
              </div>
              <Icon name="chevRight" size={14} color="var(--accent)" />
            </button>
          );
        })}
      </div>

      <SubmitTicketDialog
        open={active !== null}
        onOpenChange={(o) => !o && setActive(null)}
        boardId={active?.id ?? null}
        boardName={active?.name}
        workspaceName={active?.workspace_name}
      />
    </div>
  );
}
